import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';

import {
  Badge,
  Button,
  CaretDownIcon,
  CaretUpIcon,
  DoubleCaretVerticalIcon,
  DuplicateIcon,
  EditIcon,
  GroupObjectsIcon,
  Heading,
  Icon,
  IconButton,
  Menu,
  MinusIcon,
  MoreIcon,
  Pane,
  Popover,
  Position,
  TickCircleIcon,
  TrashIcon,
  UngroupObjectsIcon,
  Pill,
  ChevronUpIcon,
  ChevronDownIcon,
  BanCircleIcon,
  CornerDialog,
  Text,
  StatusIndicator,
  Tooltip,
  Dialog,
  Checkbox,
} from 'evergreen-ui';

import { Tooltip as MatTooltip } from '@mui/material';

import { convertDate, convertDate2, convertDate3 } from '../../helpers/dateConverter';
import { getNameAndColorEntryType } from '../../helpers/entriesHelper';
import {
  deleteEntry,
  deleteManyEntries,
  putEntry,
  putManyEntry,
  useQueryEntriesFilteredByDate,
} from '../../services/api/entriesService';
import { useQueryBanks } from '../../services/api/banksService';

import Filter from './Filter';

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getGroupedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { toastNotification } from '../../shared/toastNotification';
import EntryEditFormComponent from './EntryEditFormComponent';
import TransferEditFormComponent from './TransferEditFormComponent';
import { mutate } from 'swr';
import EntryCopyFormComponent from './EntryCopyFormComponent';
import TransferCopyFormComponent from './TransferCopyFormComponent';
import {
  isEntryReconciled,
  isTransferReconciled,
  hasReconciledEntries,
} from '../../helpers/reconciliationHelpers';
import EntryJournalApiFilterBar from './EntryJournalApiFilterBar';
import { useDispatch, useSelector } from 'react-redux';
import { dateFilterActions } from '../../store/dateFilter';
import LoadingOverlay from '../../shared/LoadingOverlay';
import { balanceValuesActions } from '../../store/balanceValues';
import { entryValuesActions } from '../../store/entryValues';
import InitialBalanceEditFormComponent from './InitialBalanceEditFormComponent';
import { useQueryUserRolesCurrentRole } from '../../services/api/userRolesService';
import { useQueryVitaUsersByCompanyId } from '../../services/api/vitaUserService';

// Extracted modules
import { createEntriesTableColumns } from './entriesTableColumns';
import { createActionHandlers } from './entriesTableActions';
import { createTableUtils, getPercentPaid, getCheckedRows } from './entriesTableUtils';
import { useEntriesTableConfig } from './entriesTableConfig';
import { EntriesTableDialogs } from './EntriesTableDialogs';

// CSS Module for table styles
import styles from './EntriesTable.module.css';

function EntriesTableComponent() {
  const dispatch = useDispatch();
  const companyId = localStorage.getItem('companyId');
  const userId = localStorage.getItem('userId');

  const startDate = useSelector((state) => state.dateFilter.startDate);
  const endDate = useSelector((state) => state.dateFilter.endDate);
  const filterType = useSelector((state) => state.dateFilter.filterType);
  const filteredEntries = useSelector((state) => state.dateFilter.filteredEntries);
  const { entryEdit, transferEdit } = useSelector((state) => state.entryValues);

  const tableContainerRef = useRef(null);
  const isMountedRef = useRef(true);

  /* API calls */

  const presetList = [
    {
      name: 'Receita Bruta',
      isInput: true,
      dreClassification: 'Receita Bruta',
      accPlans: ['AccPlan 1', 'AccPlan 2', 'AccPlan 3'],
    },
    {
      name: 'Receita Bruta',
      isInput: true,
      dreClassification: 'Receita Bruta',
      accPlans: ['AccPlan 1', 'AccPlan 2', 'AccPlan 3'],
    },
  ];

  const {
    data: journalEntriesFilteredByDate,
    isLoading: isLoadingJournalEntriesFilteredByDate,
    isValidating: isValidatingJournalEntriesFilteredByDate,
    isError: isErrorJournalEntriesFilteredByDate,
  } = useQueryEntriesFilteredByDate(
    // company.data.id,
    companyId,
    startDate,
    endDate,
    filterType,
    {
      revalidateIfStale: true,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      shouldRetryOnError: true,
    }
  );

  const { data: currentRole, isLoading: isLoadingCurrentRole } = useQueryUserRolesCurrentRole(
    { companyId: companyId, vitaUserId: userId },
    {
      revalidateIfStale: true,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const { data: banks, isLoading: isLoadingBanks } = useQueryBanks(companyId, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  const { data: vitaUsers } = useQueryVitaUsersByCompanyId(companyId, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  // Mapa id -> nome de exibição, usado pelas colunas "Criador"/"Atualizado por"
  // para não mostrar o UUID cru da VitaUser.
  const userNamesById = useMemo(() => {
    const map = new Map();
    (vitaUsers?.data || []).forEach((user) => {
      map.set(user.id, [user.name, user.lastName].filter(Boolean).join(' ') || user.email || user.id);
    });
    return map;
  }, [vitaUsers]);

  //Table states
  const [columnVisibility, setColumnVisibility] = useState({
    criadoEm: false,
    atualizadoEm: false,
    idUsuario: false,
    Agrupamento: false,
    DRE: false,
    Plano: false,
    Juros: false,
    Multa: false,
    Desconto: false,
    userCreateId: false,
    userUpdateId: false,
    Conciliado: false,
    Valor: false,
    select: false,
  });
  const [columnFilters, setColumnFilters] = useState([]);
  const [sorting, setSorting] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [grouping, setGrouping] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [columnResizeMode, setColumnResizeMode] = useState('onChange');
  const [rowSelection, setRowSelection] = useState({});

  // Entries states
  const [isShownDeleteDialog, setIsShownDeleteDialog] = useState(false);
  const [isShownDeleteManyDialog, setIsShowDeleteManyDialog] = useState(false);
  const [isShownDialog, setIsShownDialog] = useState(false);
  const [isShownCopyDialog, setIsShownCopyDialog] = useState(false);
  const [isShownTransferDialog, setIsShownTransferDialog] = useState(false);
  const [isShownInitialBalanceDialog, setIsShownInitialBalanceDialog] = useState(false);
  const [isShownTransferCopyDialog, setIsShownTransferCopyDialog] = useState(false);
  const [journalEntryOnFocus, setEntryJournalOnFocus] = useState();

  // Notification states
  const [expiredEntries, setExpiredEntries] = useState([]);
  const [isShownCorner, setIsShownCorner] = useState(false);

  const getExpiredEntries = () => {
    let counter = [];
    if (!isLoadingJournalEntriesFilteredByDate) {
      journalEntriesFilteredByDate.data.map((entry) => {
        entry.dateVencimento < convertDate(new Date()) &&
          !entry.statusPayment &&
          counter.push(entry);
      });

      setExpiredEntries(counter);
    }
  };

  useEffect(() => {
    getExpiredEntries();
  }, [
    // isLoadingJournalEntriesBankBalance,
    // journalEntriesBankBalance,
    isLoadingJournalEntriesFilteredByDate,
    journalEntriesFilteredByDate,
  ]);

  // Initialize action handlers with useMemo to keep them stable
  const actionHandlers = React.useMemo(
    () =>
      createActionHandlers(
        dispatch,
        journalEntriesFilteredByDate,
        setEntryJournalOnFocus,
        banks,
        setIsShownDialog,
        setIsShownCopyDialog,
        setIsShownTransferDialog,
        setIsShownInitialBalanceDialog,
        setIsShownTransferCopyDialog,
        setIsShownDeleteDialog,
        setIsShowDeleteManyDialog
      ),
    [
      dispatch,
      journalEntriesFilteredByDate,
      banks,
      setIsShownDialog,
      setIsShownCopyDialog,
      setIsShownTransferDialog,
      setIsShownInitialBalanceDialog,
      setIsShownTransferCopyDialog,
      setIsShownDeleteDialog,
      setIsShowDeleteManyDialog,
    ]
  );
  const {
    openDialog,
    openCopyDialog,
    openTransferDialog,
    openInitialBalanceDialog,
    openTransferCopyDialog,
    openDeleteDialog,
    openDeleteManyDialog,
    deleteMutation,
    deleteMany,
    statusPaymentMutation,
    statusPaymentManyMutation,
    checkEntries,
  } = actionHandlers;

  const [columns, setColumns] = useState([]);

  // Use table configuration hook
  const { table, processedTableData } = useEntriesTableConfig(
    columns,
    journalEntriesFilteredByDate,
    isLoadingJournalEntriesFilteredByDate,
    columnFilters,
    setColumnFilters,
    sorting,
    setSorting,
    globalFilter,
    setGlobalFilter,
    columnVisibility,
    setColumnVisibility,
    columnResizeMode,
    setColumnResizeMode,
    rowSelection,
    setRowSelection,
    grouping,
    setGrouping,
    expanded,
    setExpanded,
    getPercentPaid,
    getCheckedRows
  );

  useEffect(() => {
    if (!processedTableData || processedTableData.length === 0) {
      return;
    }

    const validIds = new Set(processedTableData.map((entry) => entry.id || entry.Id));
    setRowSelection((prev) => {
      const next = Object.keys(prev).reduce((acc, key) => {
        if (validIds.has(key)) {
          acc[key] = prev[key];
        }
        return acc;
      }, {});

      return Object.keys(next).length === Object.keys(prev).length ? prev : next;
    });
  }, [processedTableData]);

  // Initialize table utils
  const tableUtils = table ? createTableUtils(dispatch, table) : null;
  const { updateBalanceValues, selectRowHandler, selectAllHandler } = tableUtils || {};

  // Update balance values when table data changes (moved from render to useEffect)
  useEffect(() => {
    // Only update if table is fully initialized with data, columns exist, and component is mounted
    if (
      table &&
      updateBalanceValues &&
      columns.length > 0 &&
      !isLoadingJournalEntriesFilteredByDate &&
      isMountedRef.current
    ) {
      try {
        updateBalanceValues(table);
      } catch (error) {
        console.error('Error updating balance values:', error);
      }
    }
    // Dependencies are intentionally minimal to avoid infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    table,
    columns.length, // Wait until columns are created
    rowSelection, // Track when selections change
    columnFilters, // Track when filters change
    journalEntriesFilteredByDate?.data, // Track when data changes
    isLoadingJournalEntriesFilteredByDate, // Wait until data is loaded
  ]);

  // Create columns after table is available
  useEffect(() => {
    if (table) {
      const newColumns = createEntriesTableColumns(
        currentRole,
        openDialog,
        openCopyDialog,
        openTransferDialog,
        openInitialBalanceDialog,
        openTransferCopyDialog,
        openDeleteDialog,
        statusPaymentMutation,
        table,
        userNamesById
      );
      setColumns(newColumns);
    }
  }, [
    table,
    currentRole,
    openDialog,
    openCopyDialog,
    openTransferDialog,
    openInitialBalanceDialog,
    userNamesById,
    openTransferCopyDialog,
    openDeleteDialog,
    statusPaymentMutation,
  ]);

  // Set default sorting after columns are created
  useEffect(() => {
    if (table && columns.length > 0 && sorting.length === 0) {
      // Check if 'criadoEm' column exists before setting default sort
      const criadoEmColumn = table.getColumn('criadoEm');
      if (criadoEmColumn) {
        setSorting([{ id: 'criadoEm', desc: true }]);
      }
    }
  }, [table, columns, sorting.length]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  if (isLoadingJournalEntriesFilteredByDate) {
    return <LoadingOverlay />;
  }

  return (
    <>
      <Pane
        display="flex"
        flexDirection="column"
        gap={10}
        width="100%"
        padding={16}
        elevation={0}
        boxShadow="none"
      >
        <EntriesTableDialogs
          isShownDialog={isShownDialog}
          setIsShownDialog={setIsShownDialog}
          isShownCopyDialog={isShownCopyDialog}
          setIsShownCopyDialog={setIsShownCopyDialog}
          isShownTransferDialog={isShownTransferDialog}
          setIsShownTransferDialog={setIsShownTransferDialog}
          isShownInitialBalanceDialog={isShownInitialBalanceDialog}
          setIsShownInitialBalanceDialog={setIsShownInitialBalanceDialog}
          isShownTransferCopyDialog={isShownTransferCopyDialog}
          setIsShownTransferCopyDialog={setIsShownTransferCopyDialog}
          isShownDeleteDialog={isShownDeleteDialog}
          setIsShownDeleteDialog={setIsShownDeleteDialog}
          isShownDeleteManyDialog={isShownDeleteManyDialog}
          setIsShowDeleteManyDialog={setIsShowDeleteManyDialog}
          isShownCorner={isShownCorner}
          setIsShownCorner={setIsShownCorner}
          journalEntryOnFocus={journalEntryOnFocus}
          journalEntriesFilteredByDate={journalEntriesFilteredByDate}
          setEntryJournalOnFocus={setEntryJournalOnFocus}
          deleteMutation={deleteMutation}
          deleteMany={deleteMany}
          expiredEntries={expiredEntries}
          dispatch={dispatch}
        />
        <EntryJournalApiFilterBar
          tableProps={table}
          journalEntries={journalEntriesFilteredByDate}
          currentRole={currentRole}
        />

        {isValidatingJournalEntriesFilteredByDate ? (
          <LoadingOverlay />
        ) : (
          <div className={styles.tableContainer} ref={tableContainerRef}>
            <table className={styles.table}>
              <thead className={styles.tableHeader}>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      const columnId = header.column.id;
                      const isSelectColumn = columnId === 'select';
                      const isActionsColumn = columnId === 'actions';
                      const isTypeColumn = columnId === 'entryType';
                      const isValueColumn = columnId === 'Valor' || columnId === 'valorFinal';
                      const isStatusColumn = columnId === 'Status' || columnId === 'Conciliado';

                      let columnClass = '';
                      if (isSelectColumn) columnClass = styles.columnSelect;
                      else if (isTypeColumn) columnClass = styles.columnType;
                      else if (isValueColumn) columnClass = styles.columnValue;
                      else if (isActionsColumn) columnClass = styles.columnActions;
                      else if (isStatusColumn) columnClass = styles.columnStatus;
                      else if (columnId.includes('date') || columnId.includes('em'))
                        columnClass = styles.columnDate;

                      return (
                        <th key={header.id} className={columnClass}>
                          {header.isPlaceholder ? null : (
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexDirection: 'column',
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                {/* Grouping toggle */}
                                {header.column.getCanGroup() && (
                                  <MatTooltip
                                    title={header.column.getIsGrouped() ? 'Desagrupar' : 'Agrupar'}
                                    placement="top"
                                    arrow
                                  >
                                    <IconButton
                                      appearance="minimal"
                                      icon={
                                        header.column.getIsGrouped()
                                          ? UngroupObjectsIcon
                                          : GroupObjectsIcon
                                      }
                                      size="small"
                                      onClick={header.column.getToggleGroupingHandler()}
                                      intent={header.column.getIsGrouped() ? 'danger' : 'success'}
                                      className={styles.groupToggle}
                                    />
                                  </MatTooltip>
                                )}

                                {/* Select all checkbox */}
                                {isSelectColumn && (
                                  <div className={styles.selectAllContainer}>
                                    <Checkbox
                                      checked={table.getIsAllRowsSelected()}
                                      indeterminate={table.getIsSomeRowsSelected()}
                                      onChange={(e) => selectAllHandler(e, table)}
                                    />
                                  </div>
                                )}

                                {/* Column header text */}
                                {flexRender(header.column.columnDef.header, header.getContext())}

                                {/* Bulk actions menu for select column */}
                                {isSelectColumn &&
                                  table.getFilteredSelectedRowModel().flatRows.length > 1 && (
                                    <Popover
                                      position={Position.BOTTOM_LEFT}
                                      content={
                                        <Menu>
                                          <Menu.Group>
                                            <Menu.Item
                                              icon={TrashIcon}
                                              intent="danger"
                                              onClick={() => {
                                                const selectedEntries = table
                                                  .getFilteredSelectedRowModel()
                                                  .flatRows.map((item) => item.original);
                                                openDeleteManyDialog(selectedEntries);
                                              }}
                                            >
                                              Deletar selecionados
                                            </Menu.Item>
                                            {checkEntries(table) !== 'mixed' &&
                                              checkEntries(table) !== 'hasInitialBalance' && (
                                                <Menu.Item
                                                  icon={
                                                    checkEntries(table) === 'paid'
                                                      ? MinusIcon
                                                      : TickCircleIcon
                                                  }
                                                  intent={
                                                    checkEntries(table) === 'paid'
                                                      ? 'warning'
                                                      : 'success'
                                                  }
                                                  onClick={() => {
                                                    const selectedEntries = table
                                                      .getFilteredSelectedRowModel()
                                                      .flatRows.map((item) => item.original);
                                                    checkEntries(table) === 'paid'
                                                      ? statusPaymentManyMutation(
                                                          selectedEntries.map((entry) => ({
                                                            ...entry,
                                                            statusPayment: false,
                                                          }))
                                                        )
                                                      : statusPaymentManyMutation(
                                                          selectedEntries.map((entry) => ({
                                                            ...entry,
                                                            statusPayment: true,
                                                          }))
                                                        );
                                                  }}
                                                >
                                                  {checkEntries(table) === 'paid'
                                                    ? 'Marcar como Vencido'
                                                    : 'Marcar como Pago'}
                                                </Menu.Item>
                                              )}
                                          </Menu.Group>
                                        </Menu>
                                      }
                                    >
                                      <IconButton
                                        appearance="minimal"
                                        intent="info"
                                        icon={MoreIcon}
                                        size="small"
                                      />
                                    </Popover>
                                  )}

                                {/* Sort button */}
                                {header.column.getCanSort() && (
                                  <button
                                    className={styles.sortButton}
                                    onClick={header.column.getToggleSortingHandler()}
                                    aria-label={`Ordenar por ${header.column.columnDef.header}`}
                                  >
                                    {{
                                      asc: (
                                        <CaretUpIcon className={styles.sortIcon} color="selected" />
                                      ),
                                      desc: (
                                        <CaretDownIcon
                                          className={styles.sortIcon}
                                          color="selected"
                                        />
                                      ),
                                    }[header.column.getIsSorted()] ?? (
                                      <DoubleCaretVerticalIcon
                                        className={styles.sortIcon}
                                        color="muted"
                                      />
                                    )}
                                  </button>
                                )}
                              </div>

                              {/* Filter component */}
                              {header.column.getCanFilter() && (
                                <div
                                  className={styles.filterContainer}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Filter column={header.column} table={table} />
                                </div>
                              )}
                            </div>
                          )}
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>
              <tbody className={styles.tableBody}>
                {table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <tr key={row.id}>
                      {row.getVisibleCells().map((cell) => {
                        const columnId = cell.column.id;
                        const isSelectColumn = columnId === 'select';
                        const isActionsColumn = columnId === 'actions';
                        const isTypeColumn = columnId === 'entryType';
                        const isValueColumn = columnId === 'Valor' || columnId === 'valorFinal';
                        const isStatusColumn = columnId === 'Status' || columnId === 'Conciliado';

                        let columnClass = '';
                        if (isSelectColumn) columnClass = styles.columnSelect;
                        else if (isTypeColumn) columnClass = styles.columnType;
                        else if (isValueColumn) columnClass = styles.columnValue;
                        else if (isActionsColumn) columnClass = styles.columnActions;
                        else if (isStatusColumn) columnClass = styles.columnStatus;
                        else if (columnId.includes('date') || columnId.includes('em'))
                          columnClass = styles.columnDate;

                        let cellClass = columnClass;
                        if (cell.getIsGrouped()) cellClass += ` ${styles.grouped}`;
                        else if (cell.getIsAggregated()) cellClass += ` ${styles.aggregated}`;
                        else if (cell.getIsPlaceholder()) cellClass += ` ${styles.placeholder}`;

                        return (
                          <td key={cell.id} className={cellClass}>
                            {cell.getIsGrouped() ? (
                              <div className={styles.groupExpander}>
                                <button
                                  onClick={row.getToggleExpandedHandler()}
                                  aria-label={
                                    row.getIsExpanded() ? 'Recolher grupo' : 'Expandir grupo'
                                  }
                                >
                                  {row.getIsExpanded() ? <ChevronUpIcon /> : <ChevronDownIcon />}
                                </button>
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                <Pill color="#e9e9e9">{row.subRows.length}</Pill>
                              </div>
                            ) : cell.getIsAggregated() ? (
                              flexRender(
                                cell.column.columnDef.aggregatedCell ?? cell.column.columnDef.cell,
                                cell.getContext()
                              )
                            ) : cell.getIsPlaceholder() ? null : (
                              flexRender(cell.column.columnDef.cell, cell.getContext())
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={table.getAllColumns().length} className={styles.emptyState}>
                      Nenhum lançamento encontrado
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Pane>
      {/* <PaginationBarComponent tableProps={table} /> */}
    </>
  );
}

export default EntriesTableComponent;
